import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/dateUtils';

const OfficeDetailsModal = ({ office, isOpen, onClose, onEdit }) => {
  if (!isOpen || !office) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'inactive':
        return 'text-muted-foreground bg-muted';
      case 'pending':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'نشط';
      case 'inactive':
        return 'غير نشط';
      case 'pending':
        return 'معلق';
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-2xl bg-card border border-border rounded-lg shadow-elevation-3 animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                <Image
                  src={office?.profileImage}
                  alt={office?.profileImageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground font-arabic">
                  {office?.name}
                </h2>
                <p className="text-sm text-primary font-medium">
                  {office?.title}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(office)}
                iconName="Edit"
                iconPosition="left"
                iconSize={16}
              >
                تحرير
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
                iconSize={20}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">القسم</label>
                  <p className="text-sm text-foreground mt-1">{office?.department}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">الحالة</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(office?.status)}`}>
                      {getStatusLabel(office?.status)}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">تاريخ التعيين</label>
                  <p className="text-sm text-foreground mt-1">{formatDate(office?.appointmentDate)}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">البريد الإلكتروني</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <a 
                      href={`mailto:${office?.email}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {office?.email}
                    </a>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">رقم الهاتف</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <a 
                      href={`tel:${office?.phone}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {office?.phone}
                    </a>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">ترتيب العرض</label>
                  <p className="text-sm text-foreground mt-1">{office?.order}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {office?.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">الوصف</label>
                <div className="mt-2 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {office?.description}
                  </p>
                </div>
              </div>
            )}

            {/* Responsibilities */}
            {office?.responsibilities && office?.responsibilities?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">المسؤوليات</label>
                <ul className="mt-2 space-y-2">
                  {office?.responsibilities?.map((responsibility, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Metadata */}
            <div className="pt-4 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                  <span>تاريخ الإنشاء: </span>
                  <span>{formatDate(office?.createdAt)}</span>
                </div>
                <div>
                  <span>آخر تحديث: </span>
                  <span>{formatDate(office?.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={onClose}
            >
              إغلاق
            </Button>
            
            <Button
              variant="default"
              onClick={() => onEdit(office)}
              iconName="Edit"
              iconPosition="left"
              iconSize={16}
            >
              تحرير المنصب
            </Button>
          </div>
        </div>
      </div>
      {/* Click outside to close */}
      <div 
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default OfficeDetailsModal;